import { useEffect, useState } from 'react';
import { useGameStore, useLocalBubbleId, useLocalBubbleType, useRecordingBubbles, EventBus } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { Button, ControlButton, Modal } from '../ds/index.js';
import { useElapsed } from '../hooks/useElapsed.js';


interface RecordingInfo {
  id: string;
  status: string;
  startedAt?: string;
}

/** Décode le rôle depuis le JWT — même pattern dupliqué par convention dans
 *  ce fichier plutôt qu'un util partagé (voir GameHUD.tsx/GeneralChat.tsx/
 *  AdminLayout.tsx, qui font chacun leur propre décodage local). */
function decodeRole(jwt: string | null): string | null {
  if (!jwt) return null;
  try {
    const payload = JSON.parse(atob(jwt.split('.')[1]?.replace(/-/g, '+').replace(/_/g, '/') ?? ''));
    return (payload.role as string) ?? null;
  } catch {
    return null;
  }
}

/**
 * RecordingButton — bouton d'enregistrement dans le HUD.
 *
 * Visible UNIQUEMENT si le joueur local est OWNER/ADMIN (même gate que
 * canManageRecordings côté serveur — RecordingAuthorization.ts) ET dans une
 * bulle meeting-room (localBubbleType, voir bubble:changed/GameBridge.ts).
 * mapZoneId envoyé au serveur = localBubbleId (map_zones.id, Task 13) — le
 * serveur revalide indépendamment le type de zone (RecordingInvalidZoneError
 * si ce n'est pas une meeting-room), ce composant ne fait que refléter l'état
 * réel, jamais une source d'autorité.
 */
export function RecordingButton() {
  const jwt = useGameStore((s) => s.jwt);
  const localBubbleId = useLocalBubbleId();
  const localBubbleType = useLocalBubbleType();
  const recordingBubbles = useRecordingBubbles();
  const [recording, setRecording] = useState<RecordingInfo | null>(null);
  const [busy, setBusy] = useState(false);
  // Arrêter demande confirmation : un clic par erreur (ou en croyant relancer)
  // coupait la séance (Copyhouse, 29/10) ; un enregistrement arrêté ne reprend pas.
  const [confirmStop, setConfirmStop] = useState(false);
  const liveStartedAt = useGameStore((s) => (localBubbleId ? s.recordingStartedAt.get(localBubbleId) : undefined));
  const startedAt = liveStartedAt ?? (recording?.status === 'RECORDING' && recording.startedAt ? Date.parse(recording.startedAt) : undefined);
  const elapsed = useElapsed(recording?.status === 'RECORDING' ? startedAt : undefined);

  const role = decodeRole(jwt);
  const isAdmin = role ? ['owner', 'admin'].includes(role.toLowerCase()) : false;
  const isMeetingRoom = localBubbleType === 'meeting-room';
  const serverRecording = !!localBubbleId && recordingBubbles.has(localBubbleId);

  // Synchronise l'état réel (un enregistrement en cours, démarré par soi-même
  // ou un autre OWNER/ADMIN avant l'arrivée du joueur local) à l'entrée dans
  // la bulle — sans ça, le bouton mentirait ("Rec" alors qu'un enregistrement
  // tourne déjà) jusqu'au prochain clic.
  useEffect(() => {
    if (!isMeetingRoom || !isAdmin || !localBubbleId || !jwt) {
      setRecording(null);
      return;
    }
    const zoneId = useGameStore.getState().currentZoneId ?? '';
    const tenantId = zoneId.split(':')[0] ?? '';
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/media/recordings?mapZoneId=${encodeURIComponent(localBubbleId)}`, {
          headers: { Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': tenantId },
        });
        if (!res.ok || cancelled) return;
        const list = await res.json() as RecordingInfo[];
        const active = list.find((r) => r.status === 'RECORDING' || r.status === 'PROCESSING');
        if (!cancelled) setRecording(active ?? null);
      } catch {
        // Silencieux — le bouton garde son dernier état connu, le clic
        // suivant révèlera l'erreur réelle via son propre fetch.
      }
    })();
    return () => { cancelled = true; };
  // `serverRecording` relance la lecture quand un autre admin démarre ou arrête
  // l'enregistrement de la salle après notre arrivée : sans ça, le bouton
  // restait sur « Enregistrer » et un clic était refusé (Copyhouse, 2026-11-02).
  }, [isMeetingRoom, isAdmin, localBubbleId, jwt, serverRecording]);

  // Fix Important #3 (review, 2026-08-23) : sans ce garde, le bouton restait
  // bloqué sur "..." (PROCESSING) indéfiniment après un stop — rien ne
  // faisait jamais avancer l'état local au-delà de la réponse HTTP du clic
  // initial. Ne réagit QUE sur PROCESSING (jamais RECORDING) : deux bugs
  // trouvés sur VPS2 (2026-08-24) en essayant d'étendre ce garde à
  // RECORDING — (1) recordingBubbles arrive par un chemin temps réel plus
  // lent que la réponse HTTP directe (Redis → ZoneRoom → Colyseus →
  // WebSocket), donc juste après un /start réussi il peut être encore vide
  // alors que l'enregistrement tourne réellement ; (2) recordingBubbles peut
  // aussi flotter transitoirement en cours de session, sans rapport avec un
  // vrai stop. Sur PROCESSING, l'absence dans recordingBubbles est TOUJOURS
  // fiable : c'est notre propre stop qui vient de l'en retirer, aucune
  // ambiguïté possible (PROCESSING n'est atteint que localement, par notre
  // propre clic).
  useEffect(() => {
    if (localBubbleId && recording?.status === 'PROCESSING' && !recordingBubbles.has(localBubbleId)) {
      setRecording(null);
    }
  }, [recordingBubbles, localBubbleId, recording]);

  if (!isMeetingRoom || !isAdmin || !localBubbleId) return null;

  const isRecording = recording?.status === 'RECORDING';
  const isProcessing = recording?.status === 'PROCESSING';

  async function handleClick(confirmed = false) {
    if (!jwt || !localBubbleId || busy || isProcessing) return;
    if (isRecording && !confirmed) { setConfirmStop(true); return; }
    setConfirmStop(false);
    const zoneId = useGameStore.getState().currentZoneId ?? '';
    const tenantId = zoneId.split(':')[0] ?? '';
    const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}`, 'X-Tenant-ID': tenantId };

    setBusy(true);
    try {
      if (isRecording && recording) {
        // body: '{}' requis — Content-Type: application/json sans corps fait
        // rejeter la requête par Fastify (FST_ERR_CTP_EMPTY_JSON_BODY),
        // trouvé sur VPS2 (2026-08-24) : jamais atteint en local, faute
        // d'environnement où le flow start->stop réel allait assez loin.
        const res = await fetch(`${API_BASE}/media/recordings/${recording.id}/stop`, { method: 'POST', headers, body: '{}' });
        if (!res.ok) {
          EventBus.emit('ui:toast', { message: "Impossible d'arrêter l'enregistrement.", tone: 'danger' });
          return;
        }
        const updated = await res.json() as RecordingInfo;
        // Capture déjà terminée côté serveur (READY/FAILED, pas PROCESSING) :
        // le bouton redevient « Enregistrer » tout de suite.
        if (updated.status === 'PROCESSING') {
          setRecording(updated);
          EventBus.emit('ui:toast', { message: 'Enregistrement arrêté - traitement en cours.' });
        } else {
          setRecording(null);
          EventBus.emit('ui:toast', { message: "L'enregistrement s'était déjà arrêté. Tu peux le relancer.", tone: 'warning' });
        }
      } else {
        const res = await fetch(`${API_BASE}/media/recordings/start`, {
          method: 'POST', headers,
          body: JSON.stringify({ mapZoneId: localBubbleId }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({} as { error?: string }));
          const message = body.error === 'ZONE_NOT_MEETING_ROOM'
            ? "Cette zone n'est pas une salle de réunion."
            : body.error === 'RECORDER_BUSY'
              ? "Le serveur d'enregistrement est occupé. Réessaie dans une minute."
              : "Impossible de démarrer l'enregistrement.";
          EventBus.emit('ui:toast', { message, tone: 'danger' });
          return;
        }
        const created = await res.json() as RecordingInfo;
        setRecording(created);
        EventBus.emit('ui:toast', { message: 'Enregistrement démarré.', tone: 'success' });
      }
    } catch {
      EventBus.emit('ui:toast', { message: 'Erreur réseau - enregistrement non modifié.', tone: 'danger' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <ControlButton
        icon={isRecording ? 'stop' : 'record'}
        label={isRecording ? (elapsed ? `Arrêter · ${elapsed}` : 'Arrêter') : isProcessing ? 'Traitement' : 'Enregistrer'}
        off={isRecording}
        disabled={busy || isProcessing}
        title={isRecording ? `Arrêter l'enregistrement${elapsed ? ` (en cours depuis ${elapsed})` : ''}` : isProcessing ? 'Traitement en cours…' : "Démarrer l'enregistrement"}
        onClick={() => { void handleClick(); }}
      />
      {confirmStop && (
        <Modal
          title="Arrêter l'enregistrement ?"
          onClose={() => setConfirmStop(false)}
          footer={<>
            <Button variant="secondary" onClick={() => setConfirmStop(false)}>Continuer l'enregistrement</Button>
            <Button variant="danger" icon="stop" onClick={() => { void handleClick(true); }}>Arrêter</Button>
          </>}
        >
          <p style={{ margin: 0 }}>
            L'enregistrement de cette salle s'arrête pour tout le monde{elapsed ? `, après ${elapsed}` : ''}.
            Il ne pourra pas reprendre : un nouvel enregistrement créera un autre fichier.
          </p>
          <p className="pl-help" style={{ margin: '8px 0 0' }}>
            Recharger la page n'arrête pas l'enregistrement.
          </p>
        </Modal>
      )}
    </>
  );
}
